import { createFileRoute, Link } from "@tanstack/react-router";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { locations, listingsByLocation } from "@/lib/data";
import { MapPin } from "lucide-react";
import { generateBreadcrumbSchema, generateWebPageSchema } from "@/lib/utils";

const SITE_URL = "https://letslistbusiness.com";

export const Route = createFileRoute("/location/")({
  head: () => {
    const title = "Top Cities & Locations | LetsListBusiness";
    const description = "Discover the best local businesses in Austin, Portland, Brooklyn, Denver, Nashville, and Seattle. Explore top-rated services in your city.";
    const url = SITE_URL + "/location/";
    
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: "Top Cities & Locations | LetsListBusiness" },
        { property: "og:description", content: "Discover the best local businesses in Austin, Portland, Brooklyn, Denver, Nashville, and Seattle." },
        { property: "og:image", content: "https://images.unsplash.com/photo-1449034446853-66c86144b0ad?w=1200&q=80" },
        { property: "og:url", content: url },
        { property: "og:type", content: "website" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: "Top Cities & Locations | LetsListBusiness" },
        { name: "twitter:description", content: "Explore the best businesses and services across major U.S. cities." },
        { name: "twitter:image", content: "https://images.unsplash.com/photo-1449034446853-66c86144b0ad?w=1200&q=80" },
      ],
      links: [{ rel: "canonical", href: url }],
      schema: [
        generateWebPageSchema(title, description, url),
        generateBreadcrumbSchema([
          { label: "Home", href: "/" },
          { label: "Locations", href: "/location/" },
        ], SITE_URL),
      ],
    };
  },
  component: LocationsIndex,
});

// Bento spans: hero / tall / wide
const SPANS = [
  "sm:col-span-2 sm:row-span-2",
  "sm:col-span-1 sm:row-span-1",
  "sm:col-span-1 sm:row-span-1",
  "sm:col-span-1 sm:row-span-2",
  "sm:col-span-2 sm:row-span-1",
  "sm:col-span-1 sm:row-span-1",
];

function LocationsIndex() {
  return (
    <>
      <Breadcrumbs items={[{ label: "Home", href: "/" }, { label: "Locations" }]} />

      <section className="container mx-auto px-4 pb-10">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <div className="max-w-xl">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">
              <MapPin className="mr-1 inline h-3 w-3" />
              {locations.length} cities · growing weekly
            </div>
            <h1 className="mt-3 font-display text-5xl font-bold leading-[1.02] sm:text-6xl">
              Pick a city. <br />
              <em className="not-italic text-primary">Discover its best.</em>
            </h1>
          </div>
          <p className="max-w-md text-muted-foreground">
            Curated directories of locally-loved restaurants, cafés, home
            service pros, gyms and more — by people who actually live there.
          </p>
        </div>
      </section>

      {/* Bento mosaic */}
      <section className="container mx-auto px-4 pb-20">
        <div className="grid auto-rows-[180px] grid-cols-2 gap-4 sm:auto-rows-[220px] sm:grid-cols-4">
          {locations.map((l, i) => {
            const count = listingsByLocation(l.slug).length;
            return (
              <Link
                key={l.slug}
                to="/location/$slug"
                params={{ slug: l.slug }}
                className={`group relative block overflow-hidden rounded-2xl shadow-card transition-all hover:shadow-card-lg ${SPANS[i % SPANS.length]}`}
              >
                <img src={l.image} alt={`${l.name}, ${l.state}`} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                <div className="absolute inset-0 bg-linear-to-t from-black/85 via-black/30 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-5 text-white">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/80">
                    {l.state} · {count} listings
                  </div>
                  <h2 className="mt-1 font-display text-2xl font-bold leading-tight sm:text-3xl">{l.name}</h2>
                </div>
                <div className="absolute right-4 top-4 rounded-full bg-white/15 px-2.5 py-1 text-[10px] font-mono text-white backdrop-blur">
                  0{i + 1}
                </div>
              </Link>
            );
          })}
        </div>
      </section>
      {/* City Focus — expansion */}
      <section className="bg-surface py-24">
        <div className="container mx-auto px-4">
          <div className="grid gap-16 lg:grid-cols-2 lg:items-center">
            <div className="relative">
              <div className="absolute -inset-4 rounded-3xl bg-primary/10 blur-2xl" />
              <img 
                src="https://images.unsplash.com/photo-1449034446853-66c86144b0ad?w=1200&q=80" 
                alt="City landscape" 
                className="relative rounded-3xl shadow-2xl"
              />
            </div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Our Selection Criteria</div>
              <h2 className="mt-4 font-display text-4xl font-bold">How we choose our cities.</h2>
              <p className="mt-6 text-lg leading-relaxed text-muted-foreground">
                We don't aim for global domination. We aim for local expertise. We choose cities that have a distinct character, a thriving independent business community, and a population that cares deeply about where they live.
              </p>
              <div className="mt-10 grid gap-6 sm:grid-cols-2">
                {[
                  { title: "Vibrant Culture", text: "Cities with a rich history and a unique, recognizable identity." },
                  { title: "Independent Spirit", text: "A high density of locally-owned shops and services." },
                  { title: "Engaged Residents", text: "Communities that actively support and review local businesses." },
                  { title: "Growth Potential", text: "Metros that are evolving and welcoming new creative energy." },
                ].map((item) => (
                  <div key={item.title}>
                    <h3 className="font-display text-lg font-bold">{item.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Article — Internal Linking */}
      <section className="container mx-auto px-4 py-24">
        <div className="rounded-[3rem] border border-border bg-card p-1 items-center grid lg:grid-cols-2">
           <div className="p-8 sm:p-12 lg:p-16">
              <div className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">From the blog</div>
              <h2 className="mt-6 font-display text-4xl font-bold leading-tight">The ultimate city-hopping guide for local enthusiasts.</h2>
              <p className="mt-6 text-lg text-muted-foreground leading-relaxed">
                Traveling between our featured cities? We've put together a curated itinerary that highlights the best independent spots in each of our six current metros.
              </p>
              <Link 
                to="/blog/$slug" 
                params={{ slug: "local-business-guide-nashville" }} 
                className="mt-8 inline-flex items-center justify-center rounded-full bg-foreground px-8 py-4 text-sm font-bold text-background transition-transform hover:scale-105"
              >
                Read the guide
              </Link>
           </div>
           <div className="hidden lg:block h-full">
              <img 
                src="https://images.unsplash.com/photo-1514924013411-cbf25faa35bb?w=1200&q=80" 
                alt="City exploration" 
                className="h-full w-full object-cover rounded-[2.8rem]"
              />
           </div>
        </div>
      </section>
    </>
  );
}
